import { Component } from '@theme/component';

/** @typedef {{ headerMenu: HTMLElement, headerDrawerContainer: HTMLElement }} Refs */

/**
 * @extends {Component<Refs>}
 */
export class ADHeaderComponent extends Component {
    #intersectionObserver = null;
    #offscreen = false;
    #lastScrollTop = 0;
    #scrollRafId = null;
    #menuDrawerHiddenWidth = null;
    #abortController = new AbortController();

    #resizeObserver = new ResizeObserver(([entry]) => {
        if (!entry?.borderBoxSize[0]) return;
        const height = Math.round(entry.borderBoxSize[0].blockSize);
        const wrapper = this.closest('.header-wrapper');
        const wrapperHeight = wrapper ? Math.round(wrapper.getBoundingClientRect().height) : null;
        const nextHeaderHeight = `${height}px`;
        const nextStickyHeight = wrapperHeight === null ? null : `${wrapperHeight}px`;

        if (document.body.style.getPropertyValue('--header-height') !== nextHeaderHeight) {
            document.body.style.setProperty('--header-height', nextHeaderHeight);
        }
        const currentStickyHeight = document.body.style.getPropertyValue('--sticky-header-height');
        if (nextStickyHeight !== null && currentStickyHeight !== nextStickyHeight) {
            document.body.style.setProperty('--sticky-header-height', nextStickyHeight);
        }
    });

    #headerGroupResizeObserver = null;

    #observeStickyPosition = (alwaysSticky = true) => {
        if (this.#intersectionObserver) return;

        const config = {
            threshold: alwaysSticky ? 1 : 0,
        };

        this.#intersectionObserver = new IntersectionObserver(([entry]) => {
            if (!entry) return;

            const { isIntersecting } = entry;

            if (alwaysSticky) {
                this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
            } else {
                this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
            }
        }, config);

        this.#intersectionObserver.observe(this);
    };

    #handleWindowScroll = () => {
        if (this.#scrollRafId !== null) return;

        this.#scrollRafId = requestAnimationFrame(() => {
            this.#scrollRafId = null;
            this.#updateScrollState();
        });
    };

    #updateScrollState = () => {
        const stickyMode = this.getAttribute('sticky');
        if (!this.#offscreen && stickyMode !== 'always') return;

        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        const stickyElement = this.closest('.header-section') ?? this;
        const headerTop = stickyElement.getBoundingClientRect().top;
        const isScrollingUp = scrollTop < this.#lastScrollTop;
        const isAtTop = headerTop >= 0;

        if (stickyMode === 'always') {
            if (isAtTop) {
                this.dataset.scrollDirection = 'none';
            } else if (isScrollingUp) {
                this.dataset.scrollDirection = 'up';
            } else {
                this.dataset.scrollDirection = 'down';
            }

            this.#lastScrollTop = scrollTop;
            return;
        }

        if (isScrollingUp) {
            if (isAtTop) {
                this.#offscreen = false;
                this.dataset.stickyState = 'inactive';
                this.dataset.scrollDirection = 'none';
            } else {
                this.dataset.stickyState = 'active';
                this.dataset.scrollDirection = 'up';
            }
        } else if (this.dataset.stickyState === 'active') {
            this.dataset.scrollDirection = 'none';
            this.dataset.stickyState = 'idle';
        } else {
            this.dataset.scrollDirection = 'none';
            this.dataset.stickyState = 'idle';
        }

        this.#lastScrollTop = scrollTop;
    };

    #handleOverflowMinimum = (/** @type {CustomEvent} */ event) => {
        if (event.detail?.minimumReached) {
            this.#menuDrawerHiddenWidth = window.innerWidth;
        } else {
            this.#menuDrawerHiddenWidth = null;
        }
    };

    #observeHeaderGroup() {
        const headerGroup = this.closest('#header-group');
        if (!headerGroup) return;

        this.#headerGroupResizeObserver = new ResizeObserver((entries) => {
            const headerGroupHeight = entries.reduce((totalHeight, entry) => {
                if (entry.target !== this) {
                    return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
                }
                return totalHeight;
            }, 0);
            const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
            document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
        });

        this.#headerGroupResizeObserver.observe(this);

        const children = headerGroup.children;
        for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
                this.#headerGroupResizeObserver.observe(element);
            }
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.#resizeObserver.observe(this);
        this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

        this.#observeHeaderGroup();

        const stickyMode = this.getAttribute('sticky');
        if (stickyMode) {
            this.dataset.stickyState = 'inactive';
            this.#observeStickyPosition(stickyMode === 'always');

            if (stickyMode === 'scroll-up' || stickyMode === 'always') {
                document.addEventListener('scroll', this.#handleWindowScroll, {
                    signal: this.#abortController.signal,
                    passive: true,
                });
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#resizeObserver.disconnect();
        this.#intersectionObserver?.disconnect();
        this.#headerGroupResizeObserver?.disconnect();
        this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
        this.#abortController.abort();
        if (this.#scrollRafId !== null) {
            cancelAnimationFrame(this.#scrollRafId);
            this.#scrollRafId = null;
        }
    }

    updatedCallback() {
        // Re-sync after Section Rendering API re-render
    }
}

if (!customElements.get('na-header-component')) {
    customElements.define('na-header-component', ADHeaderComponent);
}
